import { Align, Button, Checkbox, Form, Justify, Label, Row } from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import AccountPreferencesButton from 'components/domain/account/AccountPreferencesButton/AccountPreferencesButton';
import Badge from 'components/foundation/Badge/badge';
import DraggableList, {
  DraggablePayload,
  IUpdatedDraggable,
} from 'components/foundation/DraggableList/draggable-list/DraggableList';
import FormAccordion from 'components/foundation/FormAccordion/FormAccordion';
import useTimesheetsColumns from 'containers/timesheets/hooks/useTimesheetsColumns';
import { useDispatch } from 'react-redux';
import {
  TimesheetView,
  updateSummaryAndTimeCardTableColumns,
  updateTimeEntryTableColumns,
} from 'store/timesheets/Timesheets';
import IIdable from 'types/Idable';
import { IVisibleTableColumn } from 'types/TableColumn';
import { t } from 'utils/localize';
import './TimesheetsColumnsForm.scss';

export interface ITimesheetsColumnsFormProps {
  className?: string | object;
  onClose: () => void;
  timesheetView: TimesheetView | null;
}

const TimesheetsColumnsForm = (props: ITimesheetsColumnsFormProps) => {
  const { className, onClose, timesheetView } = props;
  const classes = classNames('timesheets-column-form', className);

  const dispatch = useDispatch();
  const {
    summaryColumns,
    updateSummaryColumn,
    setSummaryColumns,
    timeEntryColumns,
    updateTimeEntryColumn,
    setTimeEntryColumns,
    disabledProColumns,
    saveColumns,
  } = useTimesheetsColumns();

  const actionApply = () => {
    // update member settings blob
    saveColumns();

    // update redux
    dispatch(updateSummaryAndTimeCardTableColumns(summaryColumns));
    dispatch(updateTimeEntryTableColumns(timeEntryColumns));

    onClose();
  };

  const renderDialogContent = ({ ...form }) => {
    const openSummaryTimeCard = timesheetView === TimesheetView.SUMMARY || timesheetView === TimesheetView.TIME_CARDS;
    const openTimeEntries = timesheetView === TimesheetView.TIME_ENTRIES;

    return (
      <>
        <Label>{t('Select which columns you want to show')}:</Label>

        <FormAccordion
          className="pt-4"
          title={t('Summary & Time Cards')}
          form={form}
          openByDefault={openSummaryTimeCard}
        >
          {renderColumns(summaryColumns, updateSummaryColumn, setSummaryColumns)}
        </FormAccordion>

        <FormAccordion title={t('Time Entries')} form={form} openByDefault={openTimeEntries}>
          {renderColumns(timeEntryColumns, updateTimeEntryColumn, setTimeEntryColumns)}
        </FormAccordion>

        <Row justify={Justify.SPACE_BETWEEN} align={Align.CENTER} className="mt-4">
          <Button className="mt-4" type="primary" onClick={form.handleSubmit}>
            {t('Apply')}
          </Button>
          <AccountPreferencesButton label={t('Display Preferences')} />
        </Row>
      </>
    );
  };

  const renderColumn =
    (handleOnChange: (checked: boolean, key: string) => void) =>
    (column: IVisibleTableColumn & DraggablePayload<IIdable<string>>) => {
      const disabledBecauseNotPro = disabledProColumns.some((disabledColumn) => column.key === disabledColumn.key);

      function onCheck(checked: boolean) {
        handleOnChange(checked, column.key);
      }

      const employeeGroupText = (
        <>
          <Label className="primary-text pr-1">-</Label>
          <Label className="secondary-text">{t('Summary view only')}</Label>
        </>
      );

      const payPeriodOnlyText = (
        <>
          <Label className="primary-text pr-1">-</Label>
          <Label className="secondary-text">{t('Summary view only, when viewing pay periods')}</Label>
        </>
      );

      const ptoCheckedText = (
        <>
          <Label className="primary-text pr-1">-</Label>
          <Label className="secondary-text">{t('PTO hours not included in the Regular Hours value')}</Label>
        </>
      );

      const ptoUncheckedText = (
        <>
          <Label className="primary-text pr-1">-</Label>
          <Label className="secondary-text">{t('PTO hours included in the Regular Hours value')}</Label>
        </>
      );

      return (
        <div key={column.key} className="py-2 ml-2 column-item">
          <Checkbox
            data-testid="column_checkbox"
            className="primary-text mr-0"
            checked={!disabledBecauseNotPro && column.visible}
            onChange={onCheck}
            label={column.title}
            disabled={disabledBecauseNotPro}
          />

          {column.key === 'member.memberGroup.groupName' ? employeeGroupText : ''}
          {(column.key === 'employeeSigned' || column.key === 'supervisorSigned') && payPeriodOnlyText}
          {column.key === 'paidTimeOff' && (column.visible ? ptoCheckedText : ptoUncheckedText)}
          {disabledBecauseNotPro && <Badge>{t('PRO')}</Badge>}
        </div>
      );
    };

  const renderColumns = (
    columns: IVisibleTableColumn[],
    handleOnChange: (checked: boolean, key: string) => void,
    setColumns: (columns: IVisibleTableColumn[]) => void
  ) => {
    const onColumnPositionUpdate = (columns: Array<IUpdatedDraggable<IVisibleTableColumn & IIdable<string>>>) => {
      setColumns(columns.map((column) => ({ ...column.payload, position: column.index })));
    };

    const withIds = columns.map((column) => ({ ...column, id: column.key, draggable: true }));
    return (
      <DraggableList items={withIds} onUpdate={onColumnPositionUpdate} renderItem={renderColumn(handleOnChange)} />
    );
  };

  return <Form className={classes} onSubmit={actionApply} render={renderDialogContent} />;
};

export default TimesheetsColumnsForm;
